import { Divider, TreeSelect, TreeSelectProps } from 'antd'
import React, { useMemo, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { useTreeSelectShiftClick } from '../../../shared/hooks/useTreeSelectShiftClick'
import { Company } from '../../../types/company/Company'
import { useCompanyDimensionTree } from '../queries/useCompanyDimensionTree'

interface DimensionSelectProps extends TreeSelectProps {
  companyId?: Company['id']
  virtualThreshold?: number // Threshold to enable virtual scrolling
  showHint?: boolean
  enableShiftClick?: boolean
}

const DimensionTreeSelect = ({
  companyId,
  virtualThreshold = 1000,
  showHint,
  enableShiftClick,
  ...treeSelectProps
}: DimensionSelectProps) => {
  const { t } = useTranslation()
  const { data: dimensionTree, isLoading } = useCompanyDimensionTree(companyId)

  // Count total nodes recursively
  const totalNodeCount = useMemo(() => {
    if (!dimensionTree) return 0

    const countNodes = (nodes: any[]): number => {
      return nodes.reduce((count, node) => {
        return count + 1 + (node.children ? countNodes(node.children) : 0)
      }, 0)
    }

    return countNodes(dimensionTree)
  }, [dimensionTree])

  // Memoize filter function for better performance
  const filterTreeNode = useCallback((inputVal: string, node: any) => {
    return node && node.name?.toLowerCase().includes(inputVal?.toLowerCase())
  }, [])

  // Enable virtual scrolling when node count exceeds threshold
  const shouldUseVirtual = totalNodeCount > virtualThreshold

  const { onChange: shiftClickOnChange, onDropdownVisibleChange } = useTreeSelectShiftClick({
    treeData: dimensionTree,
    fieldNames: { value: 'dimensionId', label: 'name', children: 'children' },
    onChange: treeSelectProps.onChange,
    onOpenChange: treeSelectProps.onOpenChange,
    treeCheckStrictly: treeSelectProps.treeCheckStrictly,
    labelInValue: treeSelectProps.labelInValue
  })

  return (
    <TreeSelect
      allowClear
      // Optimize tree rendering for large datasets
      showSearch={{ filterTreeNode, treeNodeFilterProp: 'name' }}
      loading={isLoading}
      // Enable virtual scrolling for large datasets
      virtual={shouldUseVirtual}
      // Optimize dropdown height and scrolling
      listHeight={shouldUseVirtual ? 256 : undefined}
      styles={{
        popup: {
          root: {
            minWidth: 350,
            maxHeight: shouldUseVirtual ? 300 : 500,
            overflow: 'auto'
          }
        }
      }}
      fieldNames={{
        label: 'name',
        value: 'dimensionId',
        children: 'children'
      }}
      showCheckedStrategy={TreeSelect.SHOW_PARENT}
      treeData={dimensionTree}
      placeholder={t('contextDrawer:not-applied')}
      {...treeSelectProps}
      onChange={enableShiftClick ? shiftClickOnChange : treeSelectProps.onChange}
      onOpenChange={onDropdownVisibleChange}
      popupRender={menu => {
        const content = treeSelectProps.popupRender ? treeSelectProps.popupRender(menu) : menu
        if (!showHint) return content
        return (
          <>
            {content}
            <Divider style={{ margin: '4px 0' }} />
            <div style={{ padding: '0 8px 2px', color: 'rgba(0, 0, 0, 0.45)', fontSize: 10 }}>
              {t('global:shift-click-select-range')}
            </div>
          </>
        )
      }}
    />
  )
}

export default DimensionTreeSelect
